import React, { FC, useEffect, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, GAPS } from "../../common/consts";
import Text, { TextVariant } from "../Display/Text";
import Select from "../DataInput/Select";
import { listExportStages } from "../../services/firebase";
import { ExportDefaultStage, ExportHubSpotStageProperty, ExportStageChoice, ExportStagesResult } from "../../types/addons";

/** The "Don't change" entry: MUI's Select shows nothing for an empty value. */
const NONE = "__none__";

const PROPERTY_LABELS: Record<ExportHubSpotStageProperty, string> = {
  lifecyclestage: "Lifecycle stage",
  hs_lead_status: "Lead status",
};

const Fields = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${GAPS.md};
  max-width: 360px;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GAPS.md};
`;

const Muted = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin: 0;
`;

const Danger = styled(Text)`
  color: var(--color-statusDangerText, ${COLORS.statusDangerText});
  margin: 0;
`;

interface Props {
  crm: "hubspot" | "outreach";
  canEdit: boolean;
  value: ExportDefaultStage | undefined;
  onChange: (next: ExportDefaultStage) => void;
}

/**
 * Settings > Export > Default stage: the stage a record gets when an export
 * creates it, and the one an existing record gets when an export updates it.
 * HubSpot: Lifecycle stage or Lead status, from the portal's own options.
 * Outreach: the prospect stage. "Don't change" leaves the stage alone.
 */
const ExportDefaultStageEditor: FC<Props> = ({ crm, canEdit, value, onChange }) => {
  const [stages, setStages] = useState<ExportStagesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!canEdit) return;
    listExportStages()
      .then((r) => {
        setStages(r);
        setError(r.error || null);
      })
      .catch(() => setError(`Could not load ${crm === "hubspot" ? "HubSpot" : "Outreach"} stages.`));
  }, [canEdit, crm]);

  const property: ExportHubSpotStageProperty = value?.property === "hs_lead_status" ? "hs_lead_status" : "lifecyclestage";
  const options = (crm === "hubspot" ? stages?.hubspot?.[property] : stages?.outreach) || [];
  const stageWord = crm === "hubspot" ? PROPERTY_LABELS[property].toLowerCase() : "stage";

  const labelOf = (choice: ExportStageChoice | null | undefined) =>
    choice ? options.find((o) => o.value === choice.value)?.label || choice.label || choice.value : "Don't change";

  /** The saved choice stays listed even when the CRM no longer offers it (or has not loaded). */
  const selectOptions = (choice: ExportStageChoice | null | undefined) => [
    { value: NONE, label: "Don't change" },
    ...(choice && !options.some((o) => o.value === choice.value) ? [{ value: choice.value, label: choice.label || choice.value }] : []),
    ...options.map((o) => ({ value: o.value, label: o.label })),
  ];

  const pick = (which: "created" | "updated", v: string) => {
    const choice = v === NONE || !v ? null : { value: v, label: options.find((o) => o.value === v)?.label || v };
    onChange({ ...(crm === "hubspot" ? { property } : {}), created: value?.created ?? null, updated: value?.updated ?? null, [which]: choice });
  };

  const note =
    crm === "hubspot" && property === "lifecyclestage"
      ? "HubSpot only moves a lifecycle stage forward, so a contact already past the stage you pick keeps theirs."
      : null;

  if (!canEdit) {
    return (
      <Fields>
        {crm === "hubspot" && (
          <Row>
            <Text variant={TextVariant.sm} style={{ margin: 0 }}>Property</Text>
            <Muted variant={TextVariant.sm}>{PROPERTY_LABELS[property]}</Muted>
          </Row>
        )}
        <Row>
          <Text variant={TextVariant.sm} style={{ margin: 0 }}>New records</Text>
          <Muted variant={TextVariant.sm}>{labelOf(value?.created)}</Muted>
        </Row>
        <Row>
          <Text variant={TextVariant.sm} style={{ margin: 0 }}>Existing records</Text>
          <Muted variant={TextVariant.sm}>{labelOf(value?.updated)}</Muted>
        </Row>
        {note && <Muted variant={TextVariant.sm}>{note}</Muted>}
      </Fields>
    );
  }

  return (
    <Fields>
      {crm === "hubspot" && (
        <Select
          fullWidth
          label="Property"
          value={property}
          onChange={(e) => {
            const next = String(e.target.value) as ExportHubSpotStageProperty;
            // Values belong to one property: switching starts both choices over.
            if (next !== property) onChange({ property: next, created: null, updated: null });
          }}
          options={(Object.keys(PROPERTY_LABELS) as ExportHubSpotStageProperty[]).map((p) => ({ value: p, label: PROPERTY_LABELS[p] }))}
        />
      )}
      <Select
        fullWidth
        label={`${crm === "hubspot" ? PROPERTY_LABELS[property] : "Stage"} for new records`}
        value={value?.created?.value || NONE}
        onChange={(e) => pick("created", String(e.target.value))}
        options={selectOptions(value?.created)}
      />
      <Select
        fullWidth
        label={`${crm === "hubspot" ? PROPERTY_LABELS[property] : "Stage"} for existing records`}
        value={value?.updated?.value || NONE}
        onChange={(e) => pick("updated", String(e.target.value))}
        options={selectOptions(value?.updated)}
      />
      {!stages && !error && <Muted variant={TextVariant.sm}>Loading {stageWord}s…</Muted>}
      {error && <Danger variant={TextVariant.sm}>{error}</Danger>}
      {note && <Muted variant={TextVariant.sm}>{note}</Muted>}
    </Fields>
  );
};

export default ExportDefaultStageEditor;
